import {
  Box,
  Button,
  Checkbox,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import React from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import {
  SESSION_FEEDBACK_TYPE_DIALOGUES,
  SESSION_FEEDBACK_TYPE_TUTOR,
  SessionAttendeeRole,
  type SessionFeedbackRatingType,
} from '@/queries/constants'
import SATSeriesQ from '@/queries/SATSeriesQ'
import SATTestQ from '@/queries/SATTestQ'
import SessionAttendeeQ from '@/queries/SessionAttendeeQ'
import SessionQ from '@/queries/SessionQ'
import TutorFollowQ from '@/queries/TutorFollowQ'
import UserQ from '@/queries/UserQ'
import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import { type Query, SQL } from '@/client/query'
import { RPCRefresh } from '@/client/rpc'
import {
  NPS_RATINGS,
  SESSION_FEEDBACK_TYPE_TO_RATING_OPTIONS,
  SESSIONS_WITH_DIALOGUES_TO_SHOW_INTERNAL_FEEDBACK,
  SESSIONS_WITH_TUTOR_TO_SHOW_INTERNAL_FEEDBACK,
  type SessionFeedbackRatingTag,
  TUTOR_SESSION_FEEDBACK_RATING_TAG_TO_LABEL,
  TUTOR_SESSION_FEEDBACK_RATING_TO_TAGS,
} from '@/client/services/sessionFeedback'

import PointsTag from '@/components/Achievements/PointsTag'
import SnoozeButton from '@/components/SnoozeButton'
import Tabs from '@/components/Tabs/Tabs'
import Alert from '@/components/UI/Alert'
import FormInputRequiredStar from '@/components/UI/Form/FormInputRequiredStar'
import Tippy from '@/components/UI/Tippy'

const SHOW_SNOOZE_BUTTON_DAYS = 7
const POINTS_PER_PART = 1

export default function SessionFeedbackForm({
  tutor,
  session,
  onSubmit,
}: {
  tutor: Query
  session: Query
  onSubmit?: () => void
}): React.ReactElement {
  const user = useUser()
  const sessionId = session(SessionQ.id) ?? 0
  const feedbackType = session(SessionQ.getSessionFeedbackType()) ?? SESSION_FEEDBACK_TYPE_TUTOR

  const tutorDisplayName = tutor(UserQ.display_name) ?? ''
  const endDate = DateTime.fromISO(`${session(SessionQ.getComputedEndDate())}`)
  const showSnoozeButton = -endDate.diffNow().as('days') < SHOW_SNOOZE_BUTTON_DAYS
  const [selectedRating, setSelectedRating] = React.useState<SessionFeedbackRatingType | null>(null)
  const [ratingTags, setRatingTags] = React.useState<SessionFeedbackRatingTag[]>([])

  const [comment, setComment] = React.useState('')
  const [nps, setNPS] = React.useState<number | null>(null)
  const [internalComment, setInternalComment] = React.useState<string | null>(null)
  const [followTutor, setFollowTutor] = React.useState(false)
  const [error, setError] = React.useState<'comment' | 'rating' | 'nps' | null>(null)

  const countSessionsWithTutor =
    session`
    COUNT(DISTINCT ${SessionAttendeeQ.session_id}) FROM ${SessionAttendeeQ}
    JOIN ${SessionQ} s2 ON ${SessionAttendeeQ.session_id} = s2.id
    WHERE
      s2.user_id = ${SessionQ.user_id} AND
      ${SessionAttendeeQ.role} = ${SessionAttendeeRole.Learner} AND
      ${SessionAttendeeQ.user_id} = ${user.id} AND
      ${SessionAttendeeQ.session_breakout_id} IS NULL
  ` ?? 0

  const countDialoguesSessionsAttended =
    session(
      SessionQ.countDialoguesAttended({
        userId: user.id,
      })
    ) ?? 0

  const isSATSeriesInLastWeek: boolean =
    session`EXISTS(SELECT 1 ${SATSeriesQ.where({
      seriesId: SessionQ.series_id,
      include: [
        {
          association: 'SATTest',
          required: true,
          where: SQL`${SATTestQ.series_end_date_max} < ${SessionQ.start_date} + INTERVAL '7 DAY'`,
        },
      ],
    })})` ?? false

  const showInternalFeedback =
    feedbackType === SESSION_FEEDBACK_TYPE_DIALOGUES
      ? SESSIONS_WITH_DIALOGUES_TO_SHOW_INTERNAL_FEEDBACK.includes(countDialoguesSessionsAttended)
      : SESSIONS_WITH_TUTOR_TO_SHOW_INTERNAL_FEEDBACK.includes(countSessionsWithTutor)

  const showBootcampNPS = showInternalFeedback && isSATSeriesInLastWeek

  const ratingOptions = SESSION_FEEDBACK_TYPE_TO_RATING_OPTIONS[feedbackType]

  // TODO: Figure out following tutors in session feedback/auditing forms.
  const isFollowingTutor =
    session`EXISTS(SELECT 1 ${TutorFollowQ.where({
      tutorId: SessionQ.user_id,
      userId: user.id,
    })})` === true

  const [handleSubmit, submitting] = useAPIWrapper(
    async () => {
      setError(null)
      if (selectedRating == null) {
        setError('rating')
        return
      }
      if (nps == null && showInternalFeedback) {
        setError('nps')
        return
      }
      if (feedbackType === SESSION_FEEDBACK_TYPE_TUTOR) {
        await RPCRefresh.submitSessionFeedbackTutor({
          sessionId,
          rating: selectedRating,
          ratingTags,
          comment,
          internalComment,
          nps,
          followTutor,
        })
      } else {
        await RPCRefresh.submitSessionFeedbackDialogues({
          sessionId,
          rating: selectedRating,
          comment,
          nps,
        })
      }
      onSubmit?.()
    },
    [
      selectedRating,
      nps,
      showInternalFeedback,
      feedbackType,
      onSubmit,
      sessionId,
      ratingTags,
      comment,
      internalComment,
      followTutor,
    ],
    'Submit Feedback'
  )

  return (
    <>
      {error != null ? (
        <Stack direction="row" spacing={2} color="fg.error" mb={5} alignItems="center">
          <FontAwesomeIcon
            icon={faExclamationCircle}
            color={chakraTokenToVar('fg.error')}
            size="lg"
          />
          <Box>Please fill out the required inputs and try again.</Box>
        </Stack>
      ) : null}
      {feedbackType === SESSION_FEEDBACK_TYPE_DIALOGUES ? (
        <Alert variant="info-blue" mb={4}>
          This feedback will not be included on your Dialogue portfolio. Be sure to fill out the
          reflection survey to get credit for your portfolio.
        </Alert>
      ) : null}
      <Stack direction="row" spacing={2}>
        <Text fontWeight={600} mb={2} color={error === 'rating' ? 'fg.error' : 'fg'}>
          {feedbackType === SESSION_FEEDBACK_TYPE_DIALOGUES
            ? 'Which best describes the conversation you had with your dialogue partner(s)?'
            : 'How helpful was this session for you?'}
          <FormInputRequiredStar />
        </Text>
        <Box>
          <PointsTag points={POINTS_PER_PART} />
        </Box>
      </Stack>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6} alignItems="stretch">
        {ratingOptions.map(({ rating, icon, label }) => (
          <Stack
            key={rating}
            rounded="md"
            border={`2px solid ${chakraTokenToVar('border.emphasized')}`}
            bg={selectedRating === rating ? 'primary.subtle' : 'bg'}
            p={3}
            cursor="pointer"
            _hover={{ bg: selectedRating === rating ? 'primary.subtle' : 'bg.subtle' }}
            spacing={2}
            direction="row"
            align="center"
            justify="center"
            height="100%"
            onClick={() => {
              setSelectedRating(rating)
              setRatingTags([])
            }}
            textAlign="center"
          >
            <FontAwesomeIcon
              icon={icon}
              color={chakraTokenToVar('primary.fg')}
              style={{ fontSize: '24px' }}
            />
            <Text color="primary.fg" fontSize="16px" fontWeight={600}>
              {label}
            </Text>
          </Stack>
        ))}
      </SimpleGrid>
      {selectedRating != null && feedbackType === SESSION_FEEDBACK_TYPE_TUTOR ? (
        <Box>
          <Stack direction="row" spacing={2}>
            <Text fontWeight={600} mb={2}>
              Tell us more!
            </Text>
          </Stack>
          <Box>
            <Wrap>
              {TUTOR_SESSION_FEEDBACK_RATING_TO_TAGS[selectedRating].map(tag => (
                <WrapItem key={tag}>
                  <Stack
                    key={tag}
                    rounded="md"
                    border={`2px solid ${chakraTokenToVar('border.emphasized')}`}
                    bg={ratingTags.includes(tag) ? 'info.subtle' : 'bg'}
                    py={1}
                    px={2}
                    cursor="pointer"
                    _hover={{ bg: ratingTags.includes(tag) ? 'info.subtle' : 'bg.subtle' }}
                    spacing={2}
                    direction="column"
                    align="center"
                    onClick={() => {
                      setRatingTags(tags => {
                        if (tags.includes(tag)) {
                          return tags.filter(t => t !== tag)
                        }
                        return tags.concat([tag])
                      })
                    }}
                    textAlign="center"
                  >
                    <Text color="fg.info" fontSize="16px" fontWeight={600}>
                      {TUTOR_SESSION_FEEDBACK_RATING_TAG_TO_LABEL[tag]}
                    </Text>
                  </Stack>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        </Box>
      ) : null}
      {selectedRating == null ? null : (
        <>
          <Stack direction="row" spacing={2} mt={6} mb={2}>
            <Text fontWeight={600} color={error === 'comment' ? 'fg.error' : 'fg'}>
              {feedbackType === SESSION_FEEDBACK_TYPE_DIALOGUES
                ? 'Please describe different views and arguments you and your partner explored.'
                : 'Would you like to share a note of feedback or gratitude with your tutor? This means a lot to them!'}
            </Text>
            <Box>
              <PointsTag points={POINTS_PER_PART} />
            </Box>
          </Stack>

          <Textarea
            placeholder={
              feedbackType === SESSION_FEEDBACK_TYPE_DIALOGUES
                ? 'My initial perspective was... but when my partner explained their personal experience with...'
                : 'Thank you for this session! I love how interactive it was.'
            }
            onChange={e => setComment(e.target.value.trim())}
          />

          {showInternalFeedback ? (
            <Box
              {...(feedbackType === SESSION_FEEDBACK_TYPE_TUTOR
                ? {
                    bg: showBootcampNPS ? 'info.subtle' : 'purple.subtle',
                    my: '16px',
                    p: '16px',
                    borderRadius: '8px',
                  }
                : {})}
            >
              {feedbackType === SESSION_FEEDBACK_TYPE_TUTOR ? (
                <Text my={2}>The following feedback will not be shared with your tutor.</Text>
              ) : null}
              <Stack direction="row" spacing={2} mt={4} mb={2}>
                <Text fontWeight={600} color={error === 'nps' ? 'fg.error' : 'fg'}>
                  {showBootcampNPS ? (
                    <>
                      Time flies—your bootcamp is almost over! How likely would you be to{' '}
                      <u>recommend an SAT Bootcamp</u> to a friend, on a scale from 0 (not likely)
                      to 10 (very likely)?
                    </>
                  ) : (
                    <>
                      How likely would you be to recommend{' '}
                      {feedbackType === SESSION_FEEDBACK_TYPE_DIALOGUES
                        ? 'Dialogues'
                        : `${tutorDisplayName}'s`}{' '}
                      sessions to a friend, on a scale from 0 (not likely) to 10 (very likely)?
                    </>
                  )}
                  <FormInputRequiredStar />
                </Text>
                <Box>
                  <PointsTag points={POINTS_PER_PART} />
                </Box>
              </Stack>
              <Tabs
                variant="rounded"
                tabs={NPS_RATINGS}
                currentTab={nps}
                onTabChange={setNPS}
                buttonProps={{
                  minWidth: 0,
                  flex: 1,
                  p: { base: '2px', sm: 'auto' },
                }}
              />
              {feedbackType === SESSION_FEEDBACK_TYPE_TUTOR ? (
                <>
                  <Stack direction="row" spacing={2} mt={6} mb={2}>
                    <Text fontWeight={600}>
                      {showBootcampNPS ? (
                        <>
                          We&apos;d love to hear more about your experience, so that we can improve
                          future SAT Bootcamps. Is there anything you&apos;d like to share?
                        </>
                      ) : (
                        <>
                          We&apos;d love to hear about your experience with {tutorDisplayName}, so
                          we can help them keep building their tutoring skills. Is there anything
                          you&apos;d like to share?
                        </>
                      )}
                    </Text>
                    <Box>
                      <PointsTag points={POINTS_PER_PART} />
                    </Box>
                  </Stack>
                  <Textarea
                    onChange={e => setInternalComment(e.target.value.trim())}
                    placeholder={
                      showBootcampNPS
                        ? 'Add your feedback here!'
                        : 'They tend to move fast and I feel bad asking them to slow down because the other learners seem okay.'
                    }
                  />
                </>
              ) : null}
            </Box>
          ) : null}

          {feedbackType === SESSION_FEEDBACK_TYPE_TUTOR && !isFollowingTutor ? (
            <Box mt={6}>
              <Checkbox onChange={e => setFollowTutor(e.target.checked)}>
                Follow{' '}
                <Text as="span" fontWeight={600}>
                  {tutorDisplayName}
                </Text>{' '}
                to receive updates on their new sessions.
              </Checkbox>
            </Box>
          ) : null}
          <Stack direction={['column', 'row']} width="100%" spacing={2} justify="flex-end" mt={4}>
            {showSnoozeButton ? (
              <SnoozeButton sessionId={sessionId} />
            ) : (
              <Tippy content={`You cannot snooze feedback after ${SHOW_SNOOZE_BUTTON_DAYS} days.`}>
                <Box>
                  <Button width={['100%', 'auto']} isDisabled>
                    Answer Tomorrow
                  </Button>
                </Box>
              </Tippy>
            )}
            <Button width={['100%', 'auto']} onClick={handleSubmit} isDisabled={submitting}>
              Submit
            </Button>
          </Stack>
        </>
      )}
    </>
  )
}
